// Import necessary React and Firebase authentication hooks
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./config/firebase-config";
import React from "react";

// React Router components for navigation
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

// Page components
import LoginPage from './components/JS/LoginPage';
import Home from "./components/JS/Home";
import Navbar from "./components/JS/Navbar";
import CategoriesView from "./components/JS/CategoriesView";
import QuestionPage from "./components/JS/CategoryPage";
import QuestionManagementPage from './components/JS/QuestionManagement';
import AddCategoryPage from './components/JS/AddCategory';
import LoadingScreen from "./LoadingScreen";

// App-wide styles
import './App.css';

/**
 * Root component of the application.
 * Uses Firebase auth to determine if the user is logged in,
 * and conditionally shows either the loading screen, login, or main routes.
 */
function App() {
  // Hook to track authentication state (user object and loading status)
  const [user, loading] = useAuthState(auth);

  // While auth is loading, show a loading screen
  if (loading) return <LoadingScreen />;

  // Once loaded, pass the user to the AuthWrapper for routing logic
  return (
    <>
      <AuthWrapper user={user} />;
    </>
  );
}

/**
 * A wrapper component that handles route access based on login state.
 * Shows the Navbar when the user is logged in and not on the login page.
 * Defines all route paths and redirects unauthenticated users to the login page.
 */
function AuthWrapper({ user }) {
  const location = useLocation();

  // Check if current path is the login page
  const isLoginPage = location.pathname === "/login";

  return (
    <>
      {/* Show navbar only when logged in and not on the login page */}
      {user && !isLoginPage && <Navbar />}

      {/* Define the application's routes */}
      <Routes>
        {/* Login route – accessible only if not logged in */}
        <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />

        {/* Home route – accessible only if logged in */}
        <Route path="/" element={user ? <Home /> : <Navigate to="/login" />} />

        {/* Category overview route */}
        <Route path="/categories" element={user ? <CategoriesView /> : <Navigate to="/login" />} />

        {/* Single category (question list) route */}
        <Route path="/categories/:categoryId" element={user ? <QuestionPage /> : <Navigate to="/login" />} />

        {/* Add new question to a category */}
        <Route path="/categories/:categoryId/add" element={user ? <QuestionManagementPage /> : <Navigate to="/login" />} />

        {/* Add a new category */}
        <Route path="/add-category" element={user ? <AddCategoryPage /> : <Navigate to="/login" />} />

        {/* Edit an existing question */}
        <Route path="/categories/:categoryId/edit/:questionId" element={user ? <QuestionManagementPage /> : <Navigate to="/login" />} />

        {/* Catch-all route: redirect based on login status */}
        <Route path="*" element={<Navigate to={user ? "/" : "/login"} />} />
      </Routes>
    </>
  );
}

export default App;
